/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import {
  FileType,
  RunningParameter,
  RunningService,
  WorkflowService,
  Parameter,
  MergeDocument,
  Service,
  RunningWorkflow,
  Workflow,
} from './workflow.service';
import { WorkflowModalResult } from '../components/modals/workflow-modal.component';
import {
  SqdStatusState,
  WorkflowStatusService,
  SqdStatus,
} from './workflow-status.service';
import { FileService } from './file.service';
import { Subscription, forkJoin, Observable } from 'rxjs';
import { formatDate } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class WorkflowHelperService {
  scanStatusSubsMap = new Map<string, Subscription>();
  fileUploadSubsMap = new Map<string, Subscription>();
  workflowExecutionSubMap = new Map<string, Subscription>();
  workflowCanceledSub: Subscription;

  constructor(
    private fileService: FileService,
    private workflowService: WorkflowService,
    private workflowStatusService: WorkflowStatusService,
  ) {
    this.workflowCanceledSub =
      this.workflowStatusService.workflowCanceled.subscribe((data) =>
        this.cancelWorkflow(data),
      );
  }

  fileTypesToStrings(x: FileType, includeAlternatives = false) {
    const fileTypeStrs: string[] = [];
    let i = 0;
    let fType: number;
    while (FileType[(fType = 1 << i++)]) {
      if (x & fType) {
        fileTypeStrs.push(FileType[fType]);

        if (includeAlternatives) {
          if (fType == FileType.jpg) {
            fileTypeStrs.push('jpg');
          }
          if (fType == FileType.tif) {
            fileTypeStrs.push('tif');
          }
        }
      }
    }
    return fileTypeStrs.sort();
  }

  fileTypesToMIMETypes(x: FileType) {
    const fileTypeMIMEs: string[] = [];
    let i = 0;
    let fType: number;
    while (FileType[(fType = 1 << i++)]) {
      if (x & fType) {
        if (fType == FileType.bmp) {
          fileTypeMIMEs.push('image/bmp');
        }
        if (fType == FileType.gif) {
          fileTypeMIMEs.push('image/gif');
        }
        if (fType == FileType.jpg) {
          fileTypeMIMEs.push('image/jpeg');
        }
        if (fType == FileType.png) {
          fileTypeMIMEs.push('image/png');
        }
        if (fType == FileType.tif) {
          fileTypeMIMEs.push('image/tiff');
        }
        if (fType == FileType.zip) {
          fileTypeMIMEs.push('application/zip');
        }
        if (fType == FileType.tar) {
          fileTypeMIMEs.push('application/x-tar');
        }
        if (fType == FileType.rar) {
          fileTypeMIMEs.push('application/vnd.rar');
        }
        if (fType == FileType.msg) {
          fileTypeMIMEs.push('application/vnd.ms-outlook');
        }
        if (fType == FileType.mp3) {
          fileTypeMIMEs.push('audio/mpeg');
        }
        if (fType == FileType.txt) {
          fileTypeMIMEs.push('text/plain');
        }
        if (fType == FileType.pdf) {
          fileTypeMIMEs.push('application/pdf');
        }
        if (fType == FileType.docx) {
          fileTypeMIMEs.push(
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          );
        }
        if (fType == FileType.pptx) {
          fileTypeMIMEs.push(
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          );
        }
        if (fType == FileType.xlsx) {
          fileTypeMIMEs.push(
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          );
        }
        if (fType == FileType.doc) {
          fileTypeMIMEs.push('application/msword');
        }
        if (fType == FileType.ppt) {
          fileTypeMIMEs.push('application/vnd.ms-powerpoint');
        }
        if (fType == FileType.xls) {
          fileTypeMIMEs.push('application/vnd.ms-excel');
        }
        if (fType == FileType.rtf) {
          fileTypeMIMEs.push('application/rtf');
          fileTypeMIMEs.push('text/rtf');
          fileTypeMIMEs.push('application/x-rtf');
          fileTypeMIMEs.push('text/richtext');
        }
      }
    }
    return fileTypeMIMEs.sort();
  }

  getOutputFileTypes(fileTypes: string[]) {
    let types = 0;
    fileTypes.forEach(function (fileType) {
      switch (fileType) {
        case 'doc':
          types = types | 262144;
          break;
        case 'docx':
          types = types | 32768;
          break;
        case 'sevenz':
          types = types | 32;
          break;
        case 'zip':
          types = types | 64;
          break;
        case 'tar':
          types = types | 128;
          break;
        case 'rar':
          types = types | 256;
          break;
        case 'email':
          types = types | 512;
          break;
        case 'eml':
          types = types | 1024;
          break;
        case 'msg':
          types = types | 2048;
          break;
        case 'mp3':
          types = types | 4096;
          break;
        case 'pdf':
          types = types | 16384;
          break;
        case 'pptx':
          types = types | 65536;
          break;
        case 'xlsx':
          types = types | 131072;
          break;
        case 'ppt':
          types = types | 524288;
          break;
        case 'xls':
          types = types | 1048576;
          break;
        case 'rtf':
          types = types | 2097152;
          break;
        default:
          break;
      }
    });

    return types;
  }

  getTranslateFileTypes(sourceFileName: string, availableInputs: FileType) {
    let ext = 'txt';

    // for combined workflow, translate's has only one predefined input type, use it.
    const exts = this.fileTypesToStrings(availableInputs);
    if (exts.length === 1) {
      ext = exts[0];
    } else {
      const pos = sourceFileName.lastIndexOf('.');
      if (pos > 0) {
        ext = sourceFileName.substr(pos + 1).toLowerCase();
      }
    }

    switch (ext) {
      case 'txt':
      case 'rtf':
      case 'doc':
      case 'googledocs':
      case 'pdf':
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'bmp':
      case 'gif':
      case 'tiff':
      case 'tif':
        ext = 'docx';
        break;
      case 'ppt':
        ext = 'pptx';
        break;
      case 'xls':
        ext = 'xlsx';
        break;
      default:
        break;
    }

    const fileTypes: {
      raw: number | null;
      asArray: { key: string; value: string }[];
      extension: { key: string; value: string };
    } = {
      raw: null,
      asArray: [],
      extension: { key: '', value: '' },
    };
    fileTypes.extension.key = `.${ext.toLowerCase()}`;
    fileTypes.extension.value = ext;

    fileTypes.raw = this.getOutputFileTypes([ext, 'pdf']);
    fileTypes.asArray = this.fileTypesToStrings(fileTypes.raw).map((e) => {
      return { key: `.${e.toLowerCase()}`, value: e };
    });
    return fileTypes;
  }

  executeWorkflow(workflowData: WorkflowModalResult) {
    const workflow = this.createRunningWorkflow(workflowData.workflow);

    if (this.fileService.hasUploadedFileId) {
      return this.executeXPSEWorkflow(workflow);
    }
    // Make temp job id, and add to workflow status service and update status
    const isMultipleFiles =
      workflow.input.maxConcatenatedDocument +
        workflow.input.maxInsertedDocument >
      0
        ? true
        : false;
    const tempJobId = Date.now().toFixed();
    const originalWorkflowId = workflow.id;
    workflow.id = tempJobId;
    // Set initiatedAt date
    workflow.initiatedAt = new Date();
    workflow.dateTimeStr = formatDate(
      workflow.initiatedAt,
      'yyyy-MM-dd hh.mm.ss aaa',
      'en-US',
    );
    this.workflowStatusService.startProcessingWorkflow(workflow);
    this.updateStatus(
      tempJobId,
      SqdStatusState.Processing,
      'SDE_UPLOADING_DOCUMENT',
      'Uploading Document.',
    );

    let obs: Observable<string | string[]>;

    if (isMultipleFiles) {
      let uploadFileRequests: Observable<string>[] = [];
      let uploadFileFromCloudRepoRequests: Observable<string>[] = [];

      // files from device
      let fileFromDevice = [];
      if (Array.isArray(workflowData.inputFile)) {
        fileFromDevice = workflowData.inputFile;
      } else if (
        workflowData.inputFile &&
        Object.keys(workflowData.inputFile).length > 0
      ) {
        fileFromDevice.push(workflowData.inputFile);
      }

      if (fileFromDevice.length) {
        uploadFileRequests = fileFromDevice.map((file) => {
          return this.fileService.uploadFile(file, '');
        });
      }

      // files from cloud repos
      if (workflowData.cloudRepoFiles && workflowData.cloudRepoFiles.length) {
        uploadFileFromCloudRepoRequests = workflowData.cloudRepoFiles.map(
          (repoFile) => {
            return this.fileService.uploadFileFromCloudRepo(repoFile);
          },
        );
      }
      // merge all requests
      const requests = [
        ...uploadFileRequests,
        ...uploadFileFromCloudRepoRequests,
      ];
      obs = forkJoin(requests);
    }
    // single file from a cloud repo
    else if (
      !isMultipleFiles &&
      workflowData.cloudRepoFiles &&
      workflowData.cloudRepoFiles.length === 1
    ) {
      const repoFile = workflowData.cloudRepoFiles[0];
      repoFile.pageRange = workflowData.selectedPages;
      obs = this.fileService.uploadFileFromCloudRepo(repoFile);
    }
    // single file from a device
    else {
      const inputFile = Array.isArray(workflowData.inputFile)
        ? workflowData.inputFile[0]
        : workflowData.inputFile;
      obs = this.fileService.uploadFile(inputFile, workflowData.selectedPages);
    }

    // Upload file
    //this.fileUploadSubsMap.set(tempJobId, this.fileService.uploadFile(workflowData.inputFile).subscribe(
    this.fileUploadSubsMap.set(
      workflow.executionId,
      obs.subscribe(
        (resp) => {
          // Upload success, update status and fileId
          const fileIds = resp.toString().split(','); // toString() with split() to handle both string or array type.
          this.updateStatus(
            tempJobId,
            SqdStatusState.Processing,
            'SDE_UPLOAD_COMPLETE',
            'Upload Complete.',
          );
          this.workflowStatusService.updateWorkflowFileId(
            tempJobId,
            fileIds[0],
          );

          // merge workflow
          if (fileIds.length > 1) {
            workflow.input.mergeDocuments = [];

            // merge cloud docs into the mergeDocuments
            if (
              workflowData.cloudRepoFiles &&
              workflowData.cloudRepoFiles.length > 0
            ) {
              workflowData.cloudRepoFiles.forEach((doc) => {
                const mdoc: MergeDocument = {
                  file: null,
                  fileName: doc.file?.name || '',
                  fileId: doc.fileId || '',
                  fileFormat: doc.fileFormat || '',
                  insertPageNumber: doc.insertPageNumber || 0,
                };
                workflow.input.mergeDocuments.push(mdoc);
              });
            }
            //merge local docs into the mergeDocuments
            else if (Array.isArray(workflowData.inputFile)) {
              workflowData.inputFile.forEach((doc) => {
                const mdoc = {
                  file: doc.file,
                  fileName: doc.name,
                  fileId: doc.fileId,
                  fileFormat: doc.fileFormat,
                  insertPageNumber: doc.insertPageNumber,
                };
                workflow.input.mergeDocuments.push(mdoc);
              });
            }
            // set fileId for all docs
            workflow.input.mergeDocuments.forEach(
              (mdoc, i) => (mdoc.fileId = fileIds[i]),
            );
          }

          // Execute original workflow with fileId
          workflow.id = originalWorkflowId;

          // just send the first file id as attachment id.

          const exeSub = this.workflowService
            .executeWorkflow(fileIds[0], workflow)
            .subscribe(
              (jobId) => {
                // Set id to new job id and start statusing workflow
                this.workflowStatusService.updateWorkflowId(
                  workflow.id || '',
                  jobId,
                );
                workflow.id = jobId;
                workflow.waitToBeCanceled = false; // enable cancelation
                this.getWorkflowStatus(jobId);
              },
              () => {
                // If execution failed, report
                this.updateStatus(
                  tempJobId,
                  SqdStatusState.Errored,
                  'SDE_ERROR_STARTING_WORKFLOW',
                  'There was an error starting workflow.',
                );
              },
            );

          // register the exeSub for cancellation
          this.workflowExecutionSubMap.set(workflow.executionId, exeSub);
        },
        (err) => {
          let errorString = 'SDE_UPLOAD_FAILED';
          if (
            err.error &&
            typeof err.error == 'string' &&
            err.error.startsWith('SDE_')
          ) {
            errorString = err.error;
          }
          // If upload failed, report
          this.updateStatus(
            tempJobId,
            SqdStatusState.Errored,
            errorString,
            'There was an error uploading file.',
          );
        },
      ),
    );
  }

  private executeXPSEWorkflow(workflow: RunningWorkflow) {
    const fileId = this.fileService.currentFileId;
    // Set workflow input fileId to current uploaded file id
    workflow.input.fileId = fileId;

    // Set initiatedAt date
    workflow.initiatedAt = new Date();
    workflow.dateTimeStr = formatDate(
      workflow.initiatedAt,
      'yyyy-MM-dd hh.mm.ss aaa',
      'en-US',
    );
    this.workflowStatusService.startProcessingWorkflow(workflow);

    this.updateStatus(
      workflow.id || '',
      SqdStatusState.Processing,
      'SDE_UPLOAD_COMPLETE',
      'Upload Complete.',
    );
    workflow.output.printOptions = null;
    this.workflowService.executeWorkflow(fileId, workflow).subscribe(
      (jobId) => {
        this.fileService.currentFileId = '';
        // Set id to new job id and start statusing workflow
        this.workflowStatusService.updateWorkflowId(workflow.id || '', jobId);
        workflow.id = jobId;
        workflow.waitToBeCanceled = false; // enable cancelation
        this.getWorkflowStatus(jobId);
      },
      () => {
        // If execution failed, report
        this.updateStatus(
          workflow.id || '',
          SqdStatusState.Errored,
          'SDE_ERROR_STARTING_WORKFLOW',
          'There was an error starting workflow.',
        );
      },
    );
  }

  private stopPollingScanStatus(jobId: string): void {
    // Delete subscription
    this.scanStatusSubsMap.get(jobId)?.unsubscribe();
    this.scanStatusSubsMap.delete(jobId);
  }

  private cancelWorkflow(executionId: string): void {
    this.fileUploadSubsMap.get(executionId)?.unsubscribe();
    this.workflowExecutionSubMap.get(executionId)?.unsubscribe();
    this.fileUploadSubsMap.delete(executionId);
    this.workflowExecutionSubMap.delete(executionId);
  }

  private getWorkflowStatus(jobId: string) {
    this.workflowStatusService.startStatusRequests(jobId);
  }

  private updateStatus(
    jobId: string,
    state: SqdStatusState,
    status: string,
    statusRaw: string,
  ): void {
    const newStatus: SqdStatus = { state, status, statusRaw };
    this.workflowStatusService.updateWorkflowStatus(jobId, [newStatus]);
  }

  private createRunningWorkflow(wf: Workflow): RunningWorkflow {
    const rwf: RunningWorkflow = {
      id: wf.id,
      // for saved workflow, use parentId (the original workflow id)
      workflowId: wf.parentId ? wf.parentId : (wf.id ?? ''),
      creator: wf.creator,
      type: wf.type,
      owner: wf.owner,
      parentId: wf.parentId,
      preferredLanguage: wf.preferredLanguage,
      documentLanguage: wf.documentLanguage,
      initiatedAt: null,
      dateTimeStr: null,
      name: wf.name,
      icon: wf.icon,
      input: wf.input,
      output: wf.output,
      services: this.createRunningServices(wf),
      outputStatus: [],
      isMissedStatus: false,
      isEncryptAndTrackDisabled: wf.isEncryptAndTrackDisabled,
      interimDownloaded: wf.interimDownloaded,
      interimContinued: false,
      waitToBeCanceled: true, // set it to be false after returning from execute workflow.
      executionId: Date.now().toFixed(),
      forceEnableCancel: false,
      printJobId: '',
      seedId: wf.seedId,
      isShared: wf.isShared,
      isOrphan: wf.isOrphan,
      isSingleService: wf.isSingleService,
      isDisabled: wf.isDisabled,
    };
    return rwf;
  }

  private createRunningServices(wf: Workflow): RunningService[] {
    const runningServices = wf.services?.map((s) => {
      if (typeof s === 'string') {
        const rs: RunningService = {
          id: '',
          parentId: '',
          outputFileFormat: '',
          seedId: '',
          name: '',
        };
        return rs;
      } else {
        const service = s as Service;
        const rs: RunningService = {
          // for saved workflow, use parentId (the original service id)
          id:
            service.creator === '00000000-0000-0000-0000-000000000000'
              ? service.id
              : service.parentId,
          seedId: service.seedId,
          parentId: service.parentId,
          name: service.name,
          outputFileFormat: service.outputFileFormat,
          status: null,
          inputSettings: this.createRunningParams(service.inputSettings || []),
          serviceSettings: this.createRunningParams(
            service.serviceSettings || [],
          ),
        };
        return rs;
      }
    });
    return runningServices;
  }

  private createRunningParams(params: Parameter[]): RunningParameter[] {
    if (!params || params.length === 0) {
      return [];
    }
    const runningParams = params.map((p) => {
      const rp: RunningParameter = {
        name: p.name,
        name2: p.name2,
        value: p.value?.toString(),
        value2: p.value2?.toString(),
        type: p.type,
        setDocumentLanguage: p.setDocumentLanguage,
      };
      return rp;
    });
    return runningParams;
  }
}
